import { yupResolver } from '@hookform/resolvers/yup';
import type { FC } from 'react';
import { useCallback } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import {
    Button,
    ButtonSize,
    ButtonVariant,
    FormInput,
    Divider,
} from '@catalyze/ui';
import type { TExpectedDecisionMaker } from 'api/types/edm';
import { edmSchema } from 'schemas/edm';

import { ButtonsContainer, Container, DeleteEDM } from './styled';

import { AddressInputFields } from '../AddressInputFields';
import { InputFieldsArray } from '../InputFieldsArray';

const PHONE_FIELDS_NAME = 'phones';
const EMAIL_FIELDS_NAME = 'emails';
const ADDRESS_FIELDS_NAME = 'addresses';

interface IEditableEDM {
    onCancel: () => void;
    onSave: (data: Partial<TExpectedDecisionMaker>) => void;
    onDelete?: (id: number) => void;
    data: TExpectedDecisionMaker;
    isNew?: boolean;
}

export const EditableEDM: FC<IEditableEDM> = ({
    onCancel,
    onSave,
    onDelete,
    data,
    isNew,
}) => {
    const formMethods = useForm({
        defaultValues: {
            firstName: data.firstName,
            lastName: data.lastName,
            dateOfBirth: data.dateOfBirth?.split('T')[0],
            [PHONE_FIELDS_NAME]: data.phones.map((phone) => {
                return { value: phone.number, updatedAt: phone.updatedAt };
            }),
            [EMAIL_FIELDS_NAME]: data.emails.map((email) => {
                return { value: email.value, updatedAt: email.updatedAt };
            }),

            [ADDRESS_FIELDS_NAME]: data.addresses.map((address) => {
                return {
                    address: address.address,
                    city: address.city,
                    state: address.state,
                    zip: address.zip,
                    updatedAt: address.updatedAt,
                };
            }),
        },
        resolver: yupResolver(edmSchema),
    });

    const { control, handleSubmit, getValues } = formMethods;

    const {
        fields: phoneFields,
        remove: removePhone,
        append: addPhone,
    } = useFieldArray({
        control,
        name: PHONE_FIELDS_NAME,
    });

    const {
        fields: emailFields,
        remove: removeEmail,
        append: addEmail,
    } = useFieldArray({
        control,
        name: EMAIL_FIELDS_NAME,
    });

    const {
        fields: addressFields,
        remove: removeAddress,
        append: addAddress,
    } = useFieldArray({
        control,
        name: ADDRESS_FIELDS_NAME,
    });

    const handleSaveForm = useCallback(() => {
        const updatedData = getValues();

        const preparedData: Partial<TExpectedDecisionMaker> = {
            firstName: updatedData.firstName,
            lastName: updatedData.lastName,
            dateOfBirth: updatedData.dateOfBirth || undefined,
            phones: updatedData[PHONE_FIELDS_NAME].map((phone) => {
                return {
                    number: phone.value,
                    dnc: false,
                    updatedAt: phone.updatedAt,
                }; // by  default all newly created phone numbers have DNS: false
            }),
            emails: updatedData[EMAIL_FIELDS_NAME].map((email) => {
                return { value: email.value, updatedAt: email.updatedAt };
            }),
            addresses: updatedData[ADDRESS_FIELDS_NAME],
        };

        onSave(preparedData);
    }, [onSave, getValues]);

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(handleSaveForm)}>
                <Container>
                    {isNew ? 'Add EDM' : 'Edit EDM'}
                    <ButtonsContainer>
                        <Button
                            size={ButtonSize.SMALL}
                            onClick={(event: MouseEvent) => {
                                event.preventDefault();
                                onCancel();
                            }}
                            variant={ButtonVariant.SECONDARY}
                        >
                            Cancel
                        </Button>
                        <Button
                            size={ButtonSize.SMALL}
                            type="submit"
                            variant={ButtonVariant.PRIMARY}
                        >
                            {isNew ? 'Add' : 'Save'}
                        </Button>
                    </ButtonsContainer>
                </Container>
                <Divider />
                <FormInput id="firstName" label="First name" />
                <FormInput id="lastName" label="Last name" />
                <FormInput id="dateOfBirth" label="Date of birth" type="date" />

                <Divider />
                <InputFieldsArray
                    name={PHONE_FIELDS_NAME}
                    addInputLabel="Add phone"
                    label="Phone"
                    inputFields={phoneFields}
                    onRemoveField={removePhone}
                    onAddField={addPhone}
                />
                <Divider />
                <InputFieldsArray
                    name={EMAIL_FIELDS_NAME}
                    addInputLabel="Add email"
                    label="Email"
                    inputFields={emailFields}
                    onRemoveField={removeEmail}
                    onAddField={addEmail}
                />
                <Divider />
                <AddressInputFields
                    addresses={addressFields}
                    onRemoveField={removeAddress}
                    onAddField={addAddress}
                />
                {!isNew && (
                    <>
                        <Divider />
                        <DeleteEDM
                            onClick={(event) => {
                                event.preventDefault();
                                onDelete?.(data.edmId);
                            }}
                        >{`Delete ${data.firstName} ${data.lastName}`}</DeleteEDM>
                    </>
                )}
            </form>
        </FormProvider>
    );
};

EditableEDM.defaultProps = {
    isNew: false,
    onDelete: undefined,
};
